import {Injectable, inject} from '@angular/core';

import {JetonContexteService} from './jeton-contexte.service';
import {ValAppSettingsService} from '../../../val-ammon-services/val-app-settings-lib/src/public-api';
import {ValApiResultat, ValBaseService} from '../../../val-ammon-services/val-services-communs-lib/src/public-api';

//Représente une application autorisée, telle que la rend la route de repli
class APrApplication
{
  //Code de l’application
  public code!: string;
}

//Représente le résultat de la résolution des applications de l’utilisateur
//  La réussite est portée par un champ distinct du contenu, et ce n’est pas une précaution
//  décorative : une liste vide obtenue par échec autoriserait tout si on la confondait avec une
//  liste vide résolue, qui est un refus légitime. L’appelant ne regarde « applications » qu’après
//  avoir vu « reussi ». Cf. ADR-0017, décision D13.
export class ResolutionApplications
{
  //Indique si la résolution a abouti
  public reussi!: boolean;

  //Codes des applications autorisées, vide tant que la résolution n’a pas abouti
  public applications!: string[];
}

//Service de résolution des applications autorisées pour l’utilisateur connecté
//  Le porteur normal de l’information est la revendication « app » du jeton de contexte. La route
//  de repli n’existe que pour les sessions sans jeton, et son résultat est conservé pour la durée
//  de la session : la garde de route est évaluée à chaque navigation, elle doit rester gratuite.
@Injectable ({providedIn: 'root'})
export class ApplicationsAutoriseesService extends ValBaseService
{
  //Création
  //  Paramètres
  //    appSettingsService : Service d’accès à la configuration de l’application
  constructor (private appSettingsService: ValAppSettingsService)
  {
    super ();
  }

  //Url de base
  protected override get BaseUrl () : string
  {
    return this.appSettingsService.Configuration.UrlApi.Permissions;
  }

  //Résout les applications autorisées pour l’utilisateur connecté
  //  Retourne
  //    Résultat de la résolution
  public async Resoudre () : Promise<ResolutionApplications>
  {
    let charge = this.jetonContexteService.ChargeUtile;

    //Jeton exploitable : la décision se prend sur la liste qu’il porte, sans aucun appel.
    //
    //Le test porte sur le jeton, pas sur la revendication, et l’écart n’est pas anodin : une
    //revendication « app » absente doit se comprendre comme « n’entre nulle part », pas comme
    //« je ne sais pas, demande à l’API ». C’est l’invariant que GenererJetonContexte documente —
    //il préfère n’émettre aucun jeton plutôt qu’un jeton partiel, précisément pour que l’absence
    //de jeton reste le seul déclencheur du repli. Brancher sur « charge.app » ferait échouer
    //ouvert là où le backend a supposé qu’on échouait fermé.
    if (charge)
      return {reussi: true, applications: charge.app ?? []};

    return await this.ResoudreParRepli ();
  }

  //Oublie les applications résolues
  public Effacer () : void
  {
    this.applicationsMemorisees = null;
    this.resolutionEnCours = null;
  }

  //Résout les applications par la route de repli, au plus une fois par chargement de page
  //  Retourne
  //    Résultat de la résolution
  //  La mémorisation est volontairement en mémoire et non dans le sessionStorage. Une liste
  //  d’autorisations qui survit à la déconnexion est un piège : dans le même onglet, l’utilisateur
  //  suivant serait évalué sur la liste du précédent tant qu’il n’a pas de jeton. La déconnexion
  //  quitte l’application, donc la mémoire part avec elle — il n’y a rien à purger, et aucun futur
  //  chemin de déconnexion n’aura à y penser.
  //
  //  Le prix est un appel de repli par chargement de page au lieu d’un par session, et seulement
  //  pour les sessions sans jeton : le chemin nominal n’appelle jamais.
  private async ResoudreParRepli () : Promise<ResolutionApplications>
  {
    if (this.applicationsMemorisees)
      return {reussi: true, applications: this.applicationsMemorisees};

    //Plusieurs navigations peuvent se présenter avant la fin du premier appel : elles partagent
    //la même promesse plutôt que d’ouvrir chacune la leur.
    this.resolutionEnCours ??= this.Appeler ();

    return await this.resolutionEnCours;
  }

  //Appelle la route de repli et mémorise le résultat s’il a abouti
  //  Retourne
  //    Résultat de la résolution
  private async Appeler () : Promise<ResolutionApplications>
  {
    let valApiResultat: ValApiResultat<APrApplication[]>;
    let applications: string[];

    try
    {
      valApiResultat = await this.ExecuterRequeteGet<APrApplication[]> ("/v1.0/user/applications");

      if (valApiResultat?.status !== "success" || !valApiResultat.resultat)
      {
        //Échec : on refuse, et on n’enregistre rien pour que la navigation suivante puisse
        //réessayer. Mémoriser l’échec fermerait l’application pour toute la session sur une
        //coupure réseau d’une seconde.
        this.resolutionEnCours = null;

        return {reussi: false, applications: []};
      }

      applications = valApiResultat.resultat.map (application => application.code);

      this.applicationsMemorisees = applications;

      return {reussi: true, applications};
    }
    catch
    {
      console.warn ("Applications de l’utilisateur indisponibles, accès refusé.");

      this.resolutionEnCours = null;

      return {reussi: false, applications: []};
    }
  }

  private readonly jetonContexteService = inject (JetonContexteService);

  //Applications résolues par le repli, pour la durée du chargement de page
  private applicationsMemorisees: string[] | null = null;

  //Appel de repli en cours, partagé par les navigations concurrentes
  private resolutionEnCours: Promise<ResolutionApplications> | null = null;
}
